import { Capacitor } from '@capacitor/core';

const AUTH_TOKEN_KEYS = ['auth_token', 'token'];
const LOGIN_ROUTE = '/auth/login';
const LOGIN_ROUTE_ALT = '/login';
const MOBILE_LOGIN_ROUTE = '/mobile/login';
export const AUTH_EXPIRED_EVENT = 'kcn:auth-expired';

function getStoredAuthToken(): string | null {
  for (const key of AUTH_TOKEN_KEYS) {
    const token = localStorage.getItem(key);
    if (token) return token;
  }
  return null;
}

function clearStoredAuthTokens(): void {
  AUTH_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
}

function isLoginPath(pathname: string): boolean {
  return (
    pathname === LOGIN_ROUTE ||
    pathname.startsWith(`${LOGIN_ROUTE}?`) ||
    pathname === LOGIN_ROUTE_ALT ||
    pathname.startsWith(`${LOGIN_ROUTE_ALT}?`) ||
    pathname === MOBILE_LOGIN_ROUTE ||
    pathname.startsWith(`${MOBILE_LOGIN_ROUTE}?`)
  );
}

function isMobileSessionBridgePath(pathname: string): boolean {
  return pathname === '/auth/mobile-session' || pathname.startsWith('/auth/mobile-session');
}

function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/') || pathname === '/home';
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function shouldAutoLogoutOnUnauthorized(input: RequestInfo | URL): boolean {
  const url = getRequestUrl(input).toLowerCase();

  // Search endpoints are public in this app; do not force a logout on 401 from them.
  if (url.includes('/api/v1/search/') || url.includes('/api/v1/properties/search')) {
    return false;
  }

  // Đăng ký FCM không được đá user ra khỏi app sau khi vừa đăng nhập.
  if (url.includes('/push/register') || url.includes('/push/unregister') || url.includes('/api/v1/push/')) {
    return false;
  }

  // Mở tab thông báo / đánh dấu đã đọc không được coi là hết phiên.
  if (url.includes('/notifications') || url.includes('/user/notifications')) {
    return false;
  }

  if (url.includes('/auth/login') || url.includes('/auth/register')) {
    return false;
  }

  return true;
}

function redirectToLogin(): void {
  try {
    if (Capacitor.isNativePlatform()) {
      // APK: 401 từ thống kê/FCM/hợp đồng không được xóa session.
      return;
    }
  } catch {
    // Ignore Capacitor detection errors.
  }

  clearStoredAuthTokens();
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));

  if (!isLoginPath(window.location.pathname)) {
    window.location.replace(LOGIN_ROUTE);
  }
}

export function installFetchInterceptor(): void {
  if (typeof window === 'undefined' || !window.fetch) return;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    const token = getStoredAuthToken();

    if (
      response.status === 401 &&
      token &&
      shouldAutoLogoutOnUnauthorized(input) &&
      !isLoginPath(window.location.pathname) &&
      !isMobileSessionBridgePath(window.location.pathname) &&
      !isAdminPath(window.location.pathname)
    ) {
      redirectToLogin();
    }
    return response;
  };
}

export function fetchWithAuth(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = getStoredAuthToken();
  const headers = new Headers(init.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(input, {
    ...init,
    headers,
  });
}
